import {useRef} from 'react';

import {Alert, SimpleGrid} from '@mantine/core';
import {I18n} from '@aws-amplify/core';

import {useProducts} from '../hooks/useProducts.ts';
import {useProductsSelection} from '../hooks/useProductsSelection.ts';
import {useProductsTableColumns} from '../hooks/useProductsTableColumns.tsx';
import {Product} from '../types';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';

// The message for an empty table. THREE different situations (§3, §13), and they must
// not read alike: nothing matches the search, the status filter hides everything loaded,
// or there are no products at all.
const emptyState = (term: string, filters: Record<string, string>, moreToLoad: boolean) => {
    const more = moreToLoad
        ? ` ${I18n.get('More products may be on a page that has not been loaded yet.')}`
        : '';
    if (term) {
        return {
            color: 'gray',
            title: I18n.get('No matching products'),
            body: `${I18n.get('Nothing in the loaded products matches')} "${term}". ${I18n.get('Clear the search to see every loaded row.')}${more}`,
        };
    }
    if (Object.keys(filters).length > 0) {
        return {
            color: 'gray',
            title: I18n.get('No products match the filter'),
            body: `${I18n.get('The filter')} (${Object.entries(filters)
                                                         .map(([key, value]) => `${key}: ${value}`)
                                                         .join(', ')}) ${I18n.get('hides every loaded product. Open Filter and choose Clear to see all statuses.')}${more}`,
        };
    }
    return {
        color: 'blue',
        title: I18n.get('No products yet'),
        body: I18n.get('A product is what a customer subscribes to; its prices hang off it. Use Add Product to create the first one.'),
    };
};

export const RecordsTable = () => {
    const {selectedRecords, setSelectedRecords} = useProductsSelection();

    const {
        items,
        visibleItems,
        keyword,
        filters,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchMoreRecords,
        handleFilterByKeyword
    } = useProducts();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const {columns} = useProductsTableColumns();

    const term = keyword.trim();
    const empty = emptyState(term, filters, nextToken !== null);

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title={I18n.get('Could not load products')}>
                    {I18n.get('The billing API did not return the products list. Try Reload; if it persists, the API may be unavailable in this environment.')}
                </Alert>
            )}

            <MantineDataTable<Product>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far, the same scope as
                // the keyword filter. Token pagination cannot reorder unseen rows.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchMoreRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            {/*
              Gated on !loading so it does not flash during the first fetch, and on !isError
              so a failed load keeps its own Alert rather than claiming there are none.
            */}
            {!loading && !isError && visibleItems.length === 0 && (
                <Alert color={empty.color} title={empty.title}>
                    {empty.body}
                </Alert>
            )}

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'product'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchMoreRecords}
            />
        </SimpleGrid>
    );
};
